import React, { useState, useEffect, useCallback } from 'react';
import { Clock, User, ArrowLeft, CheckCircle, XCircle, Home, Lock, UserPlus, QrCode, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';

const KioskView = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('keyboard'); // keyboard, users, confirm, password
  const [selectedLetter, setSelectedLetter] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [autoResetTimer, setAutoResetTimer] = useState(null);
  const [availableLetters, setAvailableLetters] = useState([]);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrCodeExpiry, setQrCodeExpiry] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [qrCodeImage, setQrCodeImage] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Auto-reset to keyboard screen after 30 seconds of inactivity
  const resetToKeyboard = useCallback(() => {
    setCurrentScreen('keyboard');
    setSelectedLetter('');
    setUsers([]);
    setSelectedUser(null);
    setCurrentSession(null);
    setError(null);
    setActionMessage('');
    setPasswordInput('');
    setPasswordError(false);
  }, []);

  // Fetch QR code token
  const fetchQRCodeToken = async () => {
    try {
      const response = await fetch(`${API_URL}/kiosk-tokens/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch QR token');
      }

      const data = await response.json();
      setQrCodeData(data.signupUrl);
      setQrCodeExpiry(new Date(data.expiresAt));
      
      // Generate QR code image
      generateQRCodeImage(data.signupUrl);
    } catch (err) {
      console.error('Error fetching QR token:', err);
      setError('Failed to load QR code. Please try again.');
    }
  };

  // Calculate time remaining until QR code expires
  const updateTimeRemaining = useCallback(() => {
    if (qrCodeExpiry) {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((qrCodeExpiry - now) / 1000));
      setTimeRemaining(remaining);
      
      // If expired, fetch new token
      if (remaining <= 0) {
        fetchQRCodeToken();
      }
    }
  }, [qrCodeExpiry]);

  // Generate QR code image
  const generateQRCodeImage = async (url) => {
    if (url) {
      try {
        const qrDataURL = await QRCode.toDataURL(url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#1e293b', // slate-800
            light: '#f8fafc' // slate-50
          },
          errorCorrectionLevel: 'M'
        });
        setQrCodeImage(qrDataURL);
      } catch (err) {
        console.error('Error generating QR code:', err);
        setQrCodeImage(null);
      }
    }
  };

  // Format time remaining as MM:SS
  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const startAutoReset = useCallback(() => {
    // Clear existing timer
    if (autoResetTimer) {
      clearTimeout(autoResetTimer);
    }
    
    // Set new timer for 30 seconds
    const timer = setTimeout(() => {
      resetToKeyboard();
    }, 30000);
    
    setAutoResetTimer(timer);
  }, [autoResetTimer, resetToKeyboard]);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (autoResetTimer) {
        clearTimeout(autoResetTimer);
      }
    };
  }, [autoResetTimer]);

  // Reset timer on any user interaction
  useEffect(() => {
    if (currentScreen !== 'keyboard' && currentScreen !== 'password') {
      startAutoReset();
    }
  }, [currentScreen, startAutoReset]);

  // Fetch all users on mount to determine available letters
  useEffect(() => {
    const fetchAvailableLetters = async () => {
      try {
        const response = await fetch(`${API_URL}/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const allUsers = await response.json();
        
        // Get unique first letters from user full names
        const letters = new Set();
        allUsers.forEach(user => {
          if (user.full_name && user.full_name.length > 0) {
            letters.add(user.full_name[0].toUpperCase());
          }
        });
        
        // Convert to sorted array
        setAvailableLetters(Array.from(letters).sort());
      } catch (err) {
        console.error('Error fetching available letters:', err);
        // Fallback to showing all letters if fetch fails
        setAvailableLetters('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''));
      }
    };

    fetchAvailableLetters();
    fetchQRCodeToken(); // Fetch QR code token on mount
  }, [API_URL]);

  // Re-entry from training: sidebar's "Back to Clock In" sets sessionStorage.kioskReturnUserId
  // and full-reloads here. We refetch that user and jump straight to the confirm screen so they
  // can immediately clock in/out without re-picking.
  useEffect(() => {
    const returnUserId = sessionStorage.getItem('kioskReturnUserId');
    if (!returnUserId) return;
    sessionStorage.removeItem('kioskReturnUserId');

    let cancelled = false;
    (async () => {
      try {
        const userRes = await fetch(`${API_URL}/users/${returnUserId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!userRes.ok) throw new Error('Failed to fetch returning user');
        const user = await userRes.json();
        if (cancelled) return;

        const sessionRes = await fetch(`${API_URL}/labor/current/${user.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const sessionData = sessionRes.ok ? await sessionRes.json() : null;
        if (cancelled) return;

        setSelectedUser(user);
        setCurrentSession(sessionData && sessionData.active ? sessionData.entry : null);
        setCurrentScreen('confirm');
      } catch (err) {
        console.error('Error restoring training-return state:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [API_URL]);

  // Update time remaining every second
  useEffect(() => {
    const interval = setInterval(updateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [updateTimeRemaining]);

  // Note: Kiosk tokens are set to expire in 100 years, so no refresh needed

  const handleLetterClick = async (letter) => {
    setSelectedLetter(letter);
    setLoading(true);
    setError(null);

    try {
      // Fetch all users and filter by first letter of full_name
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const allUsers = await response.json();
      
      // Filter users whose full_name starts with the selected letter
      const filteredUsers = allUsers.filter(user => 
        user.full_name && user.full_name.toUpperCase().startsWith(letter.toUpperCase())
      );

      setUsers(filteredUsers);
      setCurrentScreen('users');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setLoading(true);
    setError(null);

    try {
      // Check if user has an active session
      const response = await fetch(`${API_URL}/labor/current/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check clock status');
      }

      const data = await response.json();
      
      if (data.active && data.entry) {
        setCurrentSession(data.entry);
      } else {
        setCurrentSession(null);
      }
      
      setCurrentScreen('confirm');
    } catch (err) {
      console.error('Error checking user status:', err);
      setError('Failed to check clock status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClockAction = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!currentSession) {
        // Clock in
        const response = await fetch(`${API_URL}/labor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            employee_id: selectedUser.id,
            full_name: selectedUser.full_name || selectedUser.username,
            activity: 'IDLE',
            business_unit: 'IDLE',
            notes: 'Clocked in via kiosk'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to clock in');
        }

        setActionMessage(`${selectedUser.full_name} clocked in successfully!`);
      } else {
        // Clock out
        const response = await fetch(`${API_URL}/labor/${currentSession.id}/clockout`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to clock out');
        }

        setActionMessage(`${selectedUser.full_name} clocked out successfully!`);
      }

      // Show success message for 3 seconds then reset
      setTimeout(() => {
        resetToKeyboard();
      }, 3000);
    } catch (err) {
      console.error('Error with clock action:', err);
      setError('Failed to complete action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Mint a kiosk-type token scoped to the selected user and hand off to the training tile.
  // PermissionContext locks per-user kiosk sessions down to ['ksp_training'] via the
  // trainingKioskUser flag. trainingKioskAdminToken / trainingKioskAdminUser preserve the
  // admin token so the sidebar's "Back to Clock In" button can restore it.
  const handleAccessTraining = async () => {
    setLoading(true);
    setError(null);

    try {
      // Refetch the full user (need the roles array) using the admin token still in localStorage.
      const profileRes = await fetch(`${API_URL}/users/${selectedUser.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!profileRes.ok) throw new Error('Failed to fetch user profile');
      const profile = await profileRes.json();

      const roleIds = Array.isArray(profile.roles)
        ? profile.roles.map((r) => (typeof r === 'object' ? r.id : r))
        : [];

      // Snapshot the current admin token/user BEFORE we overwrite them.
      const adminToken = localStorage.getItem('token');
      const adminUser = localStorage.getItem('user');
      if (adminToken && adminUser) {
        localStorage.setItem('trainingKioskAdminToken', adminToken);
        localStorage.setItem('trainingKioskAdminUser', adminUser);
      }

      const tokenPayload = {
        id: profile.id,
        roles: roleIds,
        type: 'kiosk',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (100 * 365 * 24 * 60 * 60)
      };
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify(tokenPayload));
      const signature = btoa('training-kiosk-user-signature');
      const userToken = `${header}.${payload}.${signature}`;

      const sessionUser = {
        ...profile,
        kioskSession: true,
        trainingKioskUser: true
      };

      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(sessionUser));

      // Full reload — AuthContext + PermissionContext only read the token on mount.
      window.location.href = '/tiles/ksp_training/dashboard';
    } catch (err) {
      console.error('Error starting training session:', err);
      setError('Failed to start training session. Please try again.');
      setLoading(false);
    }
  };

  const handleHomeClick = () => {
    setCurrentScreen('password');
    setPasswordInput('');
    setPasswordError(false);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === 'KSPAdmin') {
      // Check if this is a kiosk session or regular user session
      const userJSON = localStorage.getItem('user');
      if (userJSON) {
        try {
          const user = JSON.parse(userJSON);
          if (user.kioskSession) {
            // Clear kiosk session and return to login
            console.log('🔧 KioskView: Exiting kiosk session, returning to login');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('trainingKioskAdminToken');
            localStorage.removeItem('trainingKioskAdminUser');
            sessionStorage.removeItem('kioskReturnUserId');
            navigate('/login');
          } else {
            // Regular user, go to dashboard
            navigate('/');
          }
        } catch (error) {
          // If user parsing fails, default to dashboard
          console.error('Error parsing user data:', error);
          navigate('/');
        }
      } else {
        // No user found, go to login
        navigate('/login');
      }
    } else {
      setPasswordError(true);
      setPasswordInput('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative">
      {/* Home button - visible on all screens */}
      <button
        onClick={handleHomeClick}
        className="absolute top-4 right-4 bg-slate-700 hover:bg-slate-600 text-white p-3 rounded-lg transition-colors flex items-center gap-2"
      >
        <Home className="w-5 h-5" />
        <span className="hidden sm:inline">Exit Kiosk</span>
      </button>

      {/* Keyboard Screen */}
      {currentScreen === 'keyboard' && (
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-slate-100 mb-2">Employee Clock In/Out</h1>
            <p className="text-xl text-slate-400">Select the first letter of your name</p>
          </div>
          
          {availableLetters.length === 0 ? (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <p className="mt-4 text-slate-400">Loading...</p>
            </div>
          ) : (
            <div className="grid grid-cols-6 gap-4">
              {availableLetters.map((letter) => (
                <button
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  className="bg-slate-700 hover:bg-slate-600 text-white text-2xl font-bold py-8 px-4 rounded-lg transition-colors duration-200 transform hover:scale-105"
                  disabled={loading}
                >
                  {letter}
                </button>
              ))}
            </div>
          )}
          
          {/* QR Code and Sign Up Section */}
          <div className="mt-8 flex flex-col lg:flex-row items-center justify-center gap-8">
            {/* QR Code Section */}
            <div className="text-center">
              <p className="text-slate-400 mb-4">New employee? Scan to sign up:</p>
              {qrCodeImage ? (
                <div className="bg-white p-4 rounded-lg shadow-lg inline-block">
                  <img 
                    src={qrCodeImage} 
                    alt="Sign up QR Code" 
                    className="w-48 h-48"
                  />
                  <div className="mt-3 text-center">
                    <div className="text-slate-600 text-sm font-medium">
                      QR Code expires in:
                    </div>
                    <div className={`text-lg font-bold ${timeRemaining < 300 ? 'text-red-600' : 'text-slate-700'}`}>
                      {formatTimeRemaining(timeRemaining)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-700 p-4 rounded-lg shadow-lg inline-block w-48 h-48 flex items-center justify-center">
                  <div className="text-center">
                    <QrCode className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">Loading QR Code...</p>
                  </div>
                </div>
              )}
            </div>

            {/* OR Divider */}
            <div className="flex items-center">
              <div className="bg-slate-600 h-px w-8"></div>
              <span className="text-slate-400 px-4 text-sm">OR</span>
              <div className="bg-slate-600 h-px w-8"></div>
            </div>

            {/* Manual Sign Up Button */}
            <div className="text-center">
              <p className="text-slate-400 mb-4">Click to sign up manually:</p>
              <button
                onClick={() => {
                  if (qrCodeData) {
                    // Extract token from the QR code URL
                    const url = new URL(qrCodeData);
                    const token = url.searchParams.get('token');
                    if (token) {
                      navigate(`/tiles/labor_management_system/kiosk/signup?token=${token}`);
                    } else {
                      navigate('/tiles/labor_management_system/kiosk/signup');
                    }
                  } else {
                    navigate('/tiles/labor_management_system/kiosk/signup');
                  }
                }}
                disabled={!qrCodeData}
                className={`inline-flex items-center px-6 py-3 font-semibold rounded-lg transition-colors duration-200 ${
                  qrCodeData 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                }`}
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {qrCodeData ? 'Sign Up' : 'Loading...'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users List Screen */}
      {currentScreen === 'users' && (
        <div className="w-full max-w-4xl">
          <div className="mb-6">
            <button
              onClick={resetToKeyboard}
              className="flex items-center text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-6 h-6 mr-2" />
              <span className="text-lg">Back to Keyboard</span>
            </button>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-slate-100 mb-2">Select Your Name</h1>
            <p className="text-xl text-slate-400">Names starting with "{selectedLetter}"</p>
          </div>

          {error && (
            <div className="mb-4 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-lg text-center">
              {error}
            </div>
          )}

          {users.length === 0 ? (
            <div className="text-center text-slate-400 text-xl">
              No employees found with names starting with "{selectedLetter}"
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className="bg-slate-700 hover:bg-slate-600 text-left p-6 rounded-lg transition-colors duration-200"
                  disabled={loading}
                >
                  <div className="flex items-center">
                    <User className="w-8 h-8 text-slate-400 mr-4" />
                    <div>
                      <p className="text-xl font-semibold text-white">{user.full_name}</p>
                      <p className="text-slate-400">{user.username}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Screen */}
      {currentScreen === 'confirm' && (
        <div className="w-full max-w-2xl">
          <div className="bg-slate-800 rounded-lg p-8 text-center">
            {!actionMessage ? (
              <>
                <div className="mb-6">
                  <User className="w-24 h-24 text-slate-400 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-white mb-2">{selectedUser?.full_name}</h2>
                  <p className="text-xl text-slate-400">{selectedUser?.username}</p>
                </div>

                <div className="mb-8">
                  {currentSession ? (
                    <div className="flex items-center justify-center text-green-400 text-xl">
                      <Clock className="w-6 h-6 mr-2" />
                      <span>Currently clocked in</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-red-400 text-xl">
                      <Clock className="w-6 h-6 mr-2" />
                      <span>Currently clocked out</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mb-4 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex gap-4">
                    <button
                      onClick={resetToKeyboard}
                      className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleClockAction}
                      disabled={loading}
                      className={`flex-1 font-bold py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center ${
                        currentSession
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loading ? (
                        <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <>
                          {currentSession ? (
                            <>
                              <XCircle className="w-6 h-6 mr-2" />
                              Clock Out
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-6 h-6 mr-2" />
                              Clock In
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={handleAccessTraining}
                    disabled={loading}
                    className={`w-full font-bold py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center bg-amber-600 hover:bg-amber-700 text-white ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <GraduationCap className="w-6 h-6 mr-2" />
                    Access KSP Training
                  </button>
                </div>
              </>
            ) : (
              <>
                <CheckCircle className="w-24 h-24 text-green-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-2">Success!</h2>
                <p className="text-xl text-slate-400">{actionMessage}</p>
                <p className="text-sm text-slate-500 mt-4">Returning to keyboard...</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Password Screen */}
      {currentScreen === 'password' && (
        <div className="w-full max-w-md">
          <div className="bg-slate-800 rounded-lg p-8">
            <div className="text-center mb-6">
              <Lock className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Exit Kiosk Mode</h2>
              <p className="text-slate-400">Enter admin password to continue</p>
            </div>

            {passwordError && (
              <div className="mb-4 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-lg text-center">
                Incorrect password. Please try again.
              </div>
            )}

            <form onSubmit={handlePasswordSubmit}>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                placeholder="Enter password"
                autoFocus
              />

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={resetToKeyboard}
                  className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskView;